import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";
import Rails from "@rails/ujs";
import debounce from "lodash/debounce";

export default class extends Controller {
  static targets = ["search", "filterPanel", "listsContainer", "taskContainer"];
  static values = {
    boardId: String,
    workspaceId: String,
    sortListsUrl: String,
    editable: { type: Boolean, default: false },
  };

  connect() {
    this.filterPanelVisible = false;
    this.taskSortables = new Map();
    if (this.editableValue) this.initializeSortable();
    this.initializeSearch();
    this.listenForTurboStreamUpdates();
  }

  disconnect() {
    this.debouncedSearch?.cancel();
    this.destroySortables();
  }

  // ==========================================
  // Sortable Lifecycle
  // ==========================================

  destroySortables() {
    if (this.sortableLists) {
      this.sortableLists.destroy();
      this.sortableLists = null;
    }
    this.taskSortables.forEach((s) => s.destroy());
    this.taskSortables.clear();
  }

  initializeSortable() {
    if (!this.editableValue) return;

    this.initializeListSorting();
    requestAnimationFrame(() => {
      this.initializeTaskSorting();
    });
  }

  // ==========================================
  // List Sorting
  // ==========================================

  initializeListSorting() {
    const listsContainer = this.hasListsContainerTarget
      ? this.listsContainerTarget
      : this.element.querySelector("#lists");

    if (!listsContainer) return;

    if (this.sortableLists) {
      this.sortableLists.destroy();
    }

    this.sortableLists = new Sortable(listsContainer, {
      animation: 200,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      draggable: ".kanban-list",
      handle: ".list-handle",
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-drag",
      forceFallback: true,
      fallbackOnBody: true,
      filter: "#add-list-button",
      delay: 200,
      delayOnTouchOnly: true,
      touchStartThreshold: 10,
      onStart: () => listsContainer.classList.add("is-dragging-lists"),
      onEnd: (evt) => {
        listsContainer.classList.remove("is-dragging-lists");
        this.handleListSort(evt);
      },
    });
  }

  handleListSort(evt) {
    if (evt.oldIndex === evt.newIndex) return;
    const lists = this.getUpdatedListPositions(evt);
    this.updateListPositions(this.sortListsUrlValue, lists);
  }

  getUpdatedListPositions(evt) {
    return Array.from(evt.to.children)
      .map((el, index) => {
        const listId =
          el.dataset.listId ||
          el.querySelector("[data-list-id]")?.dataset.listId;
        return listId ? { id: listId, position: index + 1 } : null;
      })
      .filter(Boolean);
  }

  updateListPositions(url, lists) {
    Rails.ajax({
      url,
      type: "PATCH",
      data: new URLSearchParams({ lists: JSON.stringify(lists) }).toString(),
      contentType: "application/x-www-form-urlencoded; charset=UTF-8",
      error: () => this.showError("Failed to update list order"),
    });
  }

  // ==========================================
  // Task Sorting
  // ==========================================

  initializeTaskSorting() {
    const taskColumns = this.element.querySelectorAll(
      "[data-sortable-group-value='tasks']"
    );

    taskColumns.forEach((el) => {
      // Skip if already initialized for this element
      if (this.taskSortables.has(el)) return;

      const sortable = new Sortable(el, {
        group: "tasks",
        animation: 250,
        easing: "cubic-bezier(0.4, 0, 0.2, 1)",
        draggable: ".task-card",
        ghostClass: "task-ghost",
        chosenClass: "task-drag-preview",
        dragClass: "task-dragging",
        swapThreshold: 0.65,
        scroll: true,
        scrollSensitivity: 60,
        scrollSpeed: 12,
        delay: 200,
        delayOnTouchOnly: true,
        touchStartThreshold: 10,
        forceFallback: true,
        fallbackOnBody: true,
        onStart: (evt) => {
          evt.item.classList.add("is-being-dragged");
          document.body.classList.add("kanban-dragging");
          this.highlightDropZones(evt.from);
        },
        onEnd: (evt) => {
          evt.item.classList.remove("is-being-dragged");
          document.body.classList.remove("kanban-dragging");
          this.clearDropZones();
          this.handleTaskSort(evt);
        },
        onAdd: (evt) => {
          this.clearDropZones();
          this.reinitializeTaskCard(evt.item);
        },
        onRemove: () => this.clearDropZones(),
      });

      this.taskSortables.set(el, sortable);
    });
  }

  handleTaskSort(evt) {
    if (this.isSamePosition(evt)) return;

    const taskId = evt.item.dataset.taskId;
    const newListId = evt.to.closest("[data-list-id]")?.dataset.listId;

    if (!taskId || !newListId) return;

    // Get accurate position from DOM
    const newPosition = Array.from(evt.to.children).indexOf(evt.item) + 1;

    const url = this.buildTaskMoveUrl(
      this.workspaceIdValue,
      this.boardIdValue,
      newListId,
      taskId
    );

    this.updateTaskPosition(url, newListId, newPosition, evt);
  }

  reinitializeTaskCard(element) {
    // Reconnect Stimulus controllers on the moved task card
    if (element.dataset.controller) {
      const controllers = element.dataset.controller.split(" ");
      controllers.forEach((controllerName) => {
        const controller =
          this.application.getControllerForElementAndIdentifier(
            element,
            controllerName
          );
        if (controller) {
          controller.disconnect();
          controller.connect();
        }
      });
    }

    element.style.pointerEvents = "auto";
    element.classList.remove("is-being-dragged", "task-dragging");
  }

  isSamePosition(evt) {
    return evt.from === evt.to && evt.oldIndex === evt.newIndex;
  }

  revertTaskPosition(evt) {
    if (evt.from && evt.item) {
      evt.from.insertBefore(evt.item, evt.from.children[evt.oldIndex]);
    }
  }

  updateTaskPosition(url, newListId, newPosition, evt) {
    Rails.ajax({
      url,
      type: "PATCH",
      data: new URLSearchParams({
        "task[list_id]": newListId,
        "task[position]": newPosition.toString(),
      }).toString(),
      contentType: "application/x-www-form-urlencoded; charset=UTF-8",
      error: () => {
        this.showError("Failed to move task");
        this.revertTaskPosition(evt);
      },
    });
  }

  buildTaskMoveUrl(workspaceId, boardId, newListId, taskId) {
    return `/kanban/${workspaceId}/boards/${boardId}/lists/${newListId}/tasks/${taskId}/move`;
  }

  // ==========================================
  // Drop Zone Highlighting
  // ==========================================

  highlightDropZones(fromEl) {
    this.element
      .querySelectorAll("[data-sortable-group-value='tasks']")
      .forEach((zone) => {
        if (zone !== fromEl) {
          zone.classList.add("drop-zone-active");
        }
      });
  }

  clearDropZones() {
    this.element
      .querySelectorAll("[data-sortable-group-value='tasks']")
      .forEach((zone) => {
        zone.classList.remove(
          "drop-zone-active",
          "drop-zone-active-dark",
          "is-dragging-over"
        );
      });
  }

  // ==========================================
  // Search
  // ==========================================

  initializeSearch() {
    this.debouncedSearch = debounce(this.performServerSearch.bind(this), 450);
  }

  search(event) {
    const query = event.target.value.trim();
    this.filterLoadedCards(query);
    this.debouncedSearch(query);
  }

  filterLoadedCards(value) {
    const query = value.toLowerCase();
    this.element.querySelectorAll(".task-card").forEach((card) => {
      const title = (
        card.dataset.taskTitle ||
        card.textContent ||
        ""
      ).toLowerCase();
      card.classList.toggle(
        "hidden",
        query.length > 0 && !title.includes(query)
      );
    });
  }

  performServerSearch(query) {
    const url = new URL(window.location.href);
    const currentQuery = url.searchParams.get("search") || "";
    if (currentQuery === query) return;

    if (query) {
      url.searchParams.set("search", query);
    } else {
      url.searchParams.delete("search");
    }

    window.Turbo.visit(url.toString(), { action: "replace" });
  }

  clearSearch() {
    if (this.hasSearchTarget) {
      this.searchTarget.value = "";
      this.debouncedSearch.cancel();
      this.filterLoadedCards("");
      this.performServerSearch("");
    }
  }

  // ==========================================
  // Turbo Stream Integration
  // ==========================================

  listenForTurboStreamUpdates() {
    // Re-initialize after form submissions (toggle_assignee, update, etc.)
    document.addEventListener("turbo:submit-end", () => {
      if (!this.editableValue) return;
      setTimeout(() => {
        this.initializeTaskSorting();
      }, 100);
    });

    // Re-initialize after ANY Turbo Stream render
    document.addEventListener("turbo:render", () => {
      if (!this.editableValue) return;
      requestAnimationFrame(() => {
        this.initializeTaskSorting();
      });
    });

    // Clean up before Turbo Stream replaces lists
    document.addEventListener("turbo:before-stream-render", (event) => {
      const target = event.target.getAttribute("target");
      if (target === "lists" || target?.startsWith("list_")) {
        this.destroySortables();
      }
    });
  }

  // ==========================================
  // Filters
  // ==========================================

  toggleFilters(event) {
    event?.stopPropagation();
    this.filterPanelVisible = !this.filterPanelVisible;

    if (this.hasFilterPanelTarget) {
      this.filterPanelTarget.classList.toggle(
        "translate-x-full",
        !this.filterPanelVisible
      );
      this.filterPanelTarget.classList.toggle(
        "translate-x-0",
        this.filterPanelVisible
      );
    }
  }

  // ==========================================
  // Notifications
  // ==========================================

  showError(message) {
    this.dispatch("notice", { detail: { message, type: "error" } });
  }
}
